import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Trash2, X, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { useLineItemCategories } from '@/hooks/useConfigurableLists';

export interface EditableItem {
  id: string;
  category: string;
  description: string;
  isReusable?: boolean;
}

interface EditableItemRowProps {
  item: EditableItem;
  isDisclaimer?: boolean;
  isSaving?: boolean;
  showReusableToggle?: boolean;
  onMove?: (id: string) => void;
  moveLabel?: string;
  onEdit: (item: EditableItem) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function EditableItemRow({
  item,
  isDisclaimer = false,
  isSaving = false,
  showReusableToggle = false,
  onMove,
  moveLabel,
  onEdit,
  onDelete,
}: EditableItemRowProps) {
  const { data: lineItemCategories } = useLineItemCategories();
  const categoryOptions = lineItemCategories.map((c) => ({ value: c, label: c }));

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editCategory, setEditCategory] = useState(item.category);
  const [editDescription, setEditDescription] = useState(item.description);
  const [editIsReusable, setEditIsReusable] = useState(item.isReusable ?? false);

  const handleStartEdit = () => {
    setEditCategory(item.category);
    setEditDescription(item.description);
    setEditIsReusable(item.isReusable ?? false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditCategory(item.category);
    setEditDescription(item.description);
    setEditIsReusable(item.isReusable ?? false);
  };

  const handleSaveEdit = async () => {
    await onEdit({
      id: item.id,
      category: isDisclaimer ? 'Disclaimer' : editCategory,
      description: editDescription,
      isReusable: showReusableToggle ? editIsReusable : item.isReusable,
    });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div ref={setNodeRef} style={style} className="border border-gray-200 rounded-lg p-3 bg-white">
        <div className="space-y-3">
          {!isDisclaimer && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <Combobox
                value={editCategory}
                onValueChange={setEditCategory}
                options={categoryOptions}
                placeholder="Select category"
                searchPlaceholder="Search categories..."
                emptyText="No category found."
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isDisclaimer ? 'Disclaimer Text' : 'Description'}
            </label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              rows={isDisclaimer ? 3 : 2}
            />
          </div>
          <div className="flex items-center justify-between">
            {showReusableToggle && (
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editIsReusable}
                  onChange={(e) => setEditIsReusable(e.target.checked)}
                  className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                Reusable
              </label>
            )}
            <div className="flex gap-2 justify-end ml-auto">
              <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group border-l-4 px-3 py-2 rounded-lg flex items-start gap-2 bg-white ${
        isDragging ? 'opacity-50 shadow-lg z-50 border-blue-500 bg-blue-50' : 'border-transparent hover:bg-blue-50/30'
      }`}
    >
      <button
        className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing pt-0.5"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className={`inline-block text-xs px-2 py-0.5 rounded-full border ${
              isDisclaimer
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-white text-gray-700 border-gray-200'
            }`}
          >
            {isDisclaimer ? 'Disclaimer' : item.category}
          </span>
          {showReusableToggle && item.isReusable && (
            <span className="inline-block text-xs px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
              Reusable
            </span>
          )}
        </div>
        <p className={`text-gray-600 text-sm mt-1.5 ${isDisclaimer ? 'line-clamp-3' : 'line-clamp-2'}`}>
          {item.description}
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {onMove && moveLabel && (
          <button
            onClick={() => onMove(item.id)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap px-1.5 py-1 rounded hover:bg-blue-50 transition-colors"
            disabled={isSaving}
          >
            {moveLabel}
          </button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleStartEdit}
          className="text-gray-500 hover:text-gray-700 h-8 w-8 p-0"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(item.id)}
          className="text-gray-500 hover:text-red-600 h-8 w-8 p-0"
          disabled={isSaving}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
